import * as React from 'react'
import { addDays, endOfMonth, format, parseISO, startOfMonth } from 'date-fns'
import { calendarDay, type CalendarDay } from '@lib/validators/temporal.validator'
import type { CalendarEventDto, VenueCalendarDto } from '@lib/dto/venue-calendar.dto'
import { useVenueCalendar } from '@/hooks/useEvents'

/**
 * A venue's calendar around the month a date picker is showing: the month with
 * a week either side, because the picker shows the neighboring months' days at
 * the edges of its grid. Booked marks and held marks both read it, so they cover
 * the same days and one picker asks once.
 */
export function useCalendarAroundMonth(venueId: string, month: CalendarDay, enabled: boolean): VenueCalendarDto | undefined {
  const anchor = parseISO(month)
  const from = format(addDays(startOfMonth(anchor), -7), 'yyyy-MM-dd')
  const to = format(addDays(endOfMonth(anchor), 7), 'yyyy-MM-dd')
  return useVenueCalendar({ venueId, from: calendarDay(from), to: calendarDay(to) }, enabled && venueId !== '').data
}

/**
 * The days around a month on which another event already holds one of these
 * tracks — the marks a date picker shows so a clash is visible before the
 * server refuses the booking (Plan View: "mark dates already booked").
 *
 * The same test the API's `overlappingEvent` applies, short of the clock: an
 * event that shares a track id with this one, not canceled (the calendar
 * already leaves those out), not this event itself. A venue-wide event — no
 * tracks — holds no lane and cannot clash, so it gets no marks and marks
 * nothing. A marked day is a warning, not a refusal: two events can share a
 * track on one day when their times do not overlap.
 */
export function useBookedDays({
  venueId,
  trackIds,
  month,
  excludeEventId = null,
}: {
  venueId: string
  trackIds: readonly string[]
  /** Any day in the month the picker is showing, as `yyyy-MM-dd`. */
  month: CalendarDay
  excludeEventId?: string | null
}): CalendarDay[] {
  const enabled = venueId !== '' && trackIds.length > 0
  const events = useCalendarAroundMonth(venueId, month, enabled)?.events

  return React.useMemo(
    () => (enabled && events ? bookedDays(events, trackIds, excludeEventId) : []),
    [enabled, events, trackIds, excludeEventId],
  )
}

/** The distinct days of the events that share a track with `trackIds`. */
export function bookedDays(
  events: readonly CalendarEventDto[],
  trackIds: readonly string[],
  excludeEventId: string | null,
): CalendarDay[] {
  const days = new Set<CalendarDay>()
  for (const event of events) {
    if (event.id === excludeEventId) continue
    if (event.trackIds.some((id) => trackIds.includes(id))) days.add(event.date)
  }
  return [...days].sort()
}
